/**
 * A reusable editor for a list of short strings (allowed origins, masking
 * selectors, scrub patterns). Add via an input + button (or Enter), remove via a
 * per-row button. Controlled: the parent owns the array.
 *
 * Visual: Claude Design `.origin-list` / `.origin-item` — mono chips on a
 * `surface-2` row with a danger-tinted remove affordance, plus a `surface-2`
 * input and a secondary "Add" button.
 */
import { useState, type KeyboardEvent, type ReactElement } from 'react';

import { Button, Input, cn } from './ui/index.js';
import { Icon } from './ui/icons.js';

export interface StringListEditorProps {
  values: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  /** Accessible name for the add input. */
  label: string;
  /** Optional validation; return an error string to reject the entry. */
  validate?: (value: string) => string | null;
  /**
   * Optional canonicalization applied on add, so a chip shows the value that
   * will actually be stored rather than the one that was typed.
   */
  normalize?: (value: string) => string;
  /**
   * Optional per-entry warning for values that are already in the list. Used to
   * flag stored entries that are malformed or will be rewritten on save —
   * existing rows are never rejected on read, so this is how they surface.
   */
  warn?: (value: string) => string | null;
}

export function StringListEditor({
  values,
  onChange,
  placeholder,
  label,
  validate,
  normalize,
  warn,
}: StringListEditorProps): ReactElement {
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);

  const add = (): void => {
    const raw = draft.trim();
    if (!raw) return;
    // Validate what was typed (so the message quotes it), store what will persist.
    const validationError = validate?.(raw) ?? null;
    if (validationError) {
      setError(validationError);
      return;
    }
    const v = normalize?.(raw) ?? raw;
    if (values.includes(v)) {
      setError('Already added');
      return;
    }
    onChange([...values, v]);
    setDraft('');
    setError(null);
  };

  const remove = (value: string): void => {
    onChange(values.filter((v) => v !== value));
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') {
      e.preventDefault();
      add();
    }
  };

  return (
    <div className="flex flex-col gap-3">
      {values.length > 0 ? (
        <ul className="flex flex-col gap-2">
          {values.map((v) => {
            const warning = warn?.(v) ?? null;
            return (
            <li
              key={v}
              className="flex flex-wrap items-center gap-2.5 rounded-md border border-border bg-surface-2 py-[7px] pl-3 pr-2 font-mono text-[12.5px] text-text"
            >
              <Icon.globe className="size-3.5 shrink-0 text-faint" />
              <code className="min-w-0 truncate font-mono">{v}</code>
              {warning ? (
                <span
                  role="alert"
                  className="order-last w-full font-sans text-[11.5px] font-semibold text-warn"
                >
                  {warning}
                </span>
              ) : null}
              <button
                type="button"
                className={cn(
                  'ml-auto shrink-0 rounded-[5px] p-[3px] text-faint transition-colors',
                  'hover:bg-danger-soft hover:text-danger',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-danger',
                )}
                aria-label={`Remove ${v}`}
                onClick={() => remove(v)}
              >
                <Icon.x className="size-3.5" />
              </button>
            </li>
            );
          })}
        </ul>
      ) : null}
      <div className="flex items-center gap-2">
        <Input
          aria-label={label}
          placeholder={placeholder}
          className="flex-1 font-mono text-[12.5px]"
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            setError(null);
          }}
          onKeyDown={onKeyDown}
        />
        <Button type="button" variant="secondary" size="sm" onClick={add}>
          <Icon.plus className="size-3.5" />
          Add
        </Button>
      </div>
      {error ? (
        <p role="alert" className="text-[11.5px] font-semibold text-danger">
          {error}
        </p>
      ) : null}
    </div>
  );
}
